import React, { useState, useEffect, Suspense, lazy, Component } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AppProvider, useApp } from "./contexts/AppContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { siteConfig } from "./config/siteConfig";
// Críticos para el primer render (visitante no autenticado): import estático.
import LandingPage from "./components/Landing/LandingPage";
import ProfileModal from "./components/Auth/ProfileModal";
import LoginModal from "./components/Auth/LoginModal";
import RegisterModal from "./components/Auth/RegisterModal";
import ForgotPasswordModal from "./components/Auth/ForgotPasswordModal";
import Skeleton from "./components/ui/Skeleton";

// Vistas/páginas pesadas: carga diferida (code-splitting) para aligerar el bundle inicial.
// Layout (Header+Sidebar) n'est rendu que pour l'utilisateur connecté → lazy pour
// sortir Header/Sidebar et le TEMARIO qu'ils importent hors du bundle de la landing.
const Layout = lazy(() => import("./components/Layout/Layout"));
const Dashboard = lazy(() => import("./components/Dashboard/Dashboard"));
const StudyLibrary = lazy(() => import("./components/Study/StudyLibrary"));
const Quizzes = lazy(() => import("./components/Quiz/Quizzes"));
const ExamMode = lazy(() => import("./components/Exam/ExamMode"));
const BillingPage = lazy(() => import("./components/Billing/BillingPage"));
const LegalPage = lazy(() => import("./components/Landing/LegalPage"));
const AIChat = lazy(() => import("./components/AI/AIChat")); // Tutor IA (premium)
const UpdatePasswordPage = lazy(() =>
  import("./components/Auth/UpdatePasswordPage")
); // Asegúrate de tener este componente

// Fallback diferido layout-matched (DESIGN-SPEC §3.8) : remplace les spinners
// ad hoc par des blocs <Skeleton> qui réservent la géométrie finale du contenu
// applicatif (rangée de stats + carte). Annule le « pop » de spinner et le CLS.
// aria-busy + sr-only annoncent le chargement sans afficher de texte visible.
const AppSkeleton = () => (
  <div
    role="status"
    aria-busy="true"
    aria-live="polite"
    className="w-full max-w-7xl mx-auto"
  >
    <span className="sr-only">{siteConfig.uiCopy.loading}</span>
    {/* En-tête de vue : titre + sous-titre */}
    <Skeleton className="h-8 w-56 max-w-[60%] rounded-xl" />
    <Skeleton className="mt-3 h-4 w-80 max-w-[80%] rounded-md" />
    {/* Rangée de cartes de stats (miroir des dashboards) */}
    <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="rounded-2xl bg-white ring-1 ring-slate-900/5 elev-1 p-6"
        >
          <Skeleton className="h-10 w-10 rounded-xl" />
          <Skeleton className="mt-5 h-7 w-24 rounded-lg" />
          <Skeleton className="mt-2 h-3.5 w-32 max-w-full rounded-md" />
        </div>
      ))}
    </div>
    {/* Panneau de contenu principal */}
    <div className="mt-8 rounded-2xl bg-white ring-1 ring-slate-900/5 elev-1 p-6">
      <Skeleton className="h-6 w-48 max-w-[70%] rounded-lg" />
      <div className="mt-6 space-y-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-4 w-full rounded-md" />
        ))}
        <Skeleton className="h-4 w-3/4 rounded-md" />
      </div>
    </div>
  </div>
);

// Fallback plein écran (chargement de session / chunk de route) : même langage
// <Skeleton>, posé sur le canvas slate-50 pour que les blocs flottent — jamais
// d'écran blanc ni de spinner. Réserve la barre supérieure + un panneau.
const FullScreenSkeleton = () => (
  <div
    role="status"
    aria-busy="true"
    aria-live="polite"
    className="min-h-screen w-full overflow-x-hidden bg-slate-50 px-4 py-6 md:px-8 md:py-8"
  >
    <span className="sr-only">{siteConfig.uiCopy.loading}</span>
    <div className="mx-auto w-full max-w-7xl">
      {/* Barre supérieure (logo + actions) */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-9 w-40 rounded-xl" />
        <Skeleton className="h-9 w-9 rounded-full" />
      </div>
      {/* Corps */}
      <div className="mt-10">
        <Skeleton className="h-8 w-56 max-w-[60%] rounded-xl" />
        <Skeleton className="mt-3 h-4 w-80 max-w-[80%] rounded-md" />
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-2xl bg-white ring-1 ring-slate-900/5 elev-1 p-6"
            >
              <Skeleton className="h-10 w-10 rounded-xl" />
              <Skeleton className="mt-5 h-7 w-24 rounded-lg" />
              <Skeleton className="mt-2 h-3.5 w-32 max-w-full rounded-md" />
            </div>
          ))}
        </div>
        <div className="mt-8 rounded-2xl bg-white ring-1 ring-slate-900/5 elev-1 p-6">
          <Skeleton className="h-6 w-48 max-w-[70%] rounded-lg" />
          <div className="mt-6 space-y-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-4 w-full rounded-md" />
            ))}
            <Skeleton className="h-4 w-3/4 rounded-md" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Error boundary: captura excepciones de render en el árbol de React y muestra
// una pantalla de recuperación en lugar de dejar la página en blanco.
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary fångade ett fel:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          className="min-h-screen w-full flex flex-col items-center justify-center bg-white px-6 text-center"
        >
          <h1 className="text-2xl font-extrabold text-slate-800">
            Något gick fel
          </h1>
          <p className="mt-3 max-w-md text-sm text-slate-500">
            Ett oväntat fel inträffade. Ladda om sidan för att fortsätta.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 rounded-xl bg-indigo-700 px-6 py-3 text-sm font-bold uppercase tracking-widest text-white transition hover:bg-indigo-800"
          >
            Ladda om
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();
  const { view, currentExam } = useApp();

  const [showProfile, setShowProfile] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgot, setShowForgot] = useState(false); // Estado para el modal de recuperar

  useEffect(() => {
    // Al montar la app, reseteamos la posición del scroll al tope
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return <FullScreenSkeleton />;
  }

  // Annonce SR du basculement skeleton → contenu (aria-live poli). Une fois
  // `loading` à false, le contenu routé est prêt : on l'expose aux lecteurs
  // d'écran sans aucun texte visible (sr-only). Copie pilotée par siteConfig.
  const liveStatus = loading
    ? siteConfig.uiCopy.loading
    : siteConfig.uiCopy.ready || siteConfig.uiCopy.loading;

  const renderMainContent = () => {
    if (!isAuthenticated) {
      return (
        <div className="min-h-screen w-full overflow-x-hidden bg-slate-50">
          <LandingPage
            onLoginClick={() => setShowLogin(true)}
            onRegisterClick={() => setShowRegister(true)}
          />
          <LoginModal
            isOpen={showLogin}
            onClose={() => setShowLogin(false)}
            onSwitchToRegister={() => {
              setShowLogin(false);
              setShowRegister(true);
            }}
            onForgotPassword={() => {
              setShowLogin(false);
              setShowForgot(true);
            }}
          />
          <RegisterModal
            isOpen={showRegister}
            onClose={() => setShowRegister(false)}
            onSwitchToLogin={() => {
              setShowRegister(false);
              setShowLogin(true);
            }}
          />
          <ForgotPasswordModal
            isOpen={showForgot}
            onClose={() => setShowForgot(false)}
          />
        </div>
      );
    }

    const renderView = () => {
      if (view === "exam" && currentExam) return <ExamMode />;
      switch (view) {
        case "dashboard":
          return <Dashboard />;
        case "study":
          return <StudyLibrary />;
        case "quizzes":
          return <Quizzes />;
        case "billing":
          return <BillingPage />;
        default:
          return <Dashboard />;
      }
    };

    return (
      <div className="min-h-screen w-full flex flex-col overflow-x-hidden bg-slate-50">
        <Layout setShowProfile={setShowProfile}>
          <div className="flex-grow w-full max-w-full overflow-x-hidden">
            <Suspense fallback={<AppSkeleton />}>{renderView()}</Suspense>
          </div>
        </Layout>
        <Suspense fallback={null}>
          <AIChat />
        </Suspense>
        <ProfileModal
          isOpen={showProfile}
          onClose={() => setShowProfile(false)}
        />
      </div>
    );
  };

  return (
    <div className="crossfade-in">
      {/* Région live SR-only : annonce le passage skeleton → contenu prêt. */}
      <span className="sr-only" role="status" aria-live="polite">
        {liveStatus}
      </span>
      <Suspense fallback={<FullScreenSkeleton />}>
        <Routes>
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/update-password" element={<UpdatePasswordPage />} />
          <Route path="*" element={renderMainContent()} />
        </Routes>
      </Suspense>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppProvider>
          <Router>
            <AppContent />
          </Router>
        </AppProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
